// Geometria do gráfico de área ("montanha") do dashboard. Funções puras: recebem os pontos já em
// pixels e devolvem os caminhos SVG. O componente só desenha.

export interface Ponto {
  x: number;
  y: number;
}

/**
 * Curva suave que passa por todos os pontos sem "passar do ponto" (interpolação monotônica de
 * Fritsch–Carlson): entre dois valores a curva nunca sobe acima do maior nem desce abaixo do menor,
 * então nunca mostra um uso negativo nem um pico que não existe.
 */
export function caminhoSuave(pontos: Ponto[]): string {
  if (pontos.length === 0) return '';
  if (pontos.length === 1) return `M${pontos[0].x},${pontos[0].y}`;

  const n = pontos.length;
  const dx: number[] = [];
  const inclinacao: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pontos[i + 1].x - pontos[i].x);
    inclinacao.push((pontos[i + 1].y - pontos[i].y) / (dx[i] || 1));
  }

  // Tangente em cada ponto: zero onde a curva muda de direção (topo ou vale).
  const tangente: number[] = new Array(n).fill(0);
  tangente[0] = inclinacao[0];
  tangente[n - 1] = inclinacao[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (inclinacao[i - 1] * inclinacao[i] <= 0) {
      tangente[i] = 0;
    } else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      tangente[i] = (w1 + w2) / (w1 / inclinacao[i - 1] + w2 / inclinacao[i]);
    }
  }

  const arredondar = (v: number) => Math.round(v * 100) / 100;
  let d = `M${arredondar(pontos[0].x)},${arredondar(pontos[0].y)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pontos[i];
    const p1 = pontos[i + 1];
    const c1x = p0.x + dx[i] / 3;
    const c1y = p0.y + (tangente[i] * dx[i]) / 3;
    const c2x = p1.x - dx[i] / 3;
    const c2y = p1.y - (tangente[i + 1] * dx[i]) / 3;
    d += ` C${arredondar(c1x)},${arredondar(c1y)} ${arredondar(c2x)},${arredondar(c2y)} ${arredondar(p1.x)},${arredondar(p1.y)}`;
  }
  return d;
}

/** A mesma curva fechada até a linha de base, para o preenchimento da área. */
export function caminhoDaArea(pontos: Ponto[], base: number): string {
  if (pontos.length === 0) return '';
  const linha = caminhoSuave(pontos);
  const primeiro = pontos[0];
  const ultimo = pontos[pontos.length - 1];
  return `${linha} L${ultimo.x},${base} L${primeiro.x},${base} Z`;
}

/** Topo "redondo" do eixo Y: 7 → 8, 23 → 25, 140 → 150. Nunca menos que 1. */
export function topoDoEixo(maximo: number): number {
  if (maximo <= 1) return 1;
  const potencia = 10 ** Math.floor(Math.log10(maximo));
  for (const passo of [1, 2, 2.5, 5, 10]) {
    const topo = passo * potencia;
    if (topo >= maximo) return topo;
  }
  return 10 * potencia;
}

/** Índice do ponto mais perto do X do mouse (o "crosshair" encaixa no ponto). */
export function pontoMaisProximo(xs: number[], x: number): number {
  let melhor = 0;
  for (let i = 1; i < xs.length; i++) {
    if (Math.abs(xs[i] - x) < Math.abs(xs[melhor] - x)) melhor = i;
  }
  return melhor;
}
